import { OnchainEntityPick } from '../onchain/onchain-entity-pick';
import { useState, type ReactNode } from 'react';
import type { DecisionSummary, RunDetail, RunKind, RunSummary } from '@ni/shared';
import { isCancelledRun, isWithdrawnRun } from '../../lib/run-state';
import {
  FACET_PHASE_LABELS,
  facetLabel,
  ORG_LOOKUP_DONE,
  ORG_LOOKUP_FAILED,
  ORG_RUN_LABELS,
  orgLookupRunningLead,
  reviewBeforeBriefLine,
  RUN_BLOCK_NOTES,
  RUN_CANCEL,
  RUN_CONFIRM_IDENTITY,
  RUN_PICK_ONCHAIN_IDENTITY,
  RUN_DETAIL_LABEL,
  RUN_MORE_DETAIL,
  runBlockLabel,
  runCompleteHead,
  RUN_UNDELIVERED_HEAD,
  undeliveredLine,
  sectionsDoneLine,
} from '../../lib/copy';
import { formatClock } from '../../lib/format';
import { PickList } from '../candidates/pick-list';
import { MoreProsePrompt } from '../candidates/more-prose';
import { FindingReview } from '../adjudication/finding-review';
import {
  DedupLine,
  FacetLedger,
  orgLookupDepth,
  ResumeAction,
  StopRun,
  stripLine,
  timelineRows,
  useRunControls,
} from './run-strip';
import '../brief/evidence.css';
import './run-block.css';

/**
 * **The person's run block** (blend spec, step 4): the tinted strip's
 * replacement on the Research tab. Hairline-ruled, on the research grid's three
 * columns — the run's label over the navigator, its status over the panel, Stop
 * over the aside.
 *
 * It is a layout over the strip's own controls (`useRunControls`), so the
 * Resume window, the Stop confirmation and the questions a parked run waits on
 * behave exactly as they do on every other surface. An organization's lookup
 * and investigation use it, and so do an event's research and attendee scans.
 *
 * The meter counts sections done out of the plan and nothing else: a section in
 * flight carries a slow sweep that says "working", never "how far", and nothing
 * here estimates time. Copper only when the office has to act.
 */

export type Tone = 'working' | 'quiet' | 'act' | 'stopped' | 'done';

const WORKING_PHASES = new Set(['retrieving', 'extracting', 'posting']);

function failedWord(run: RunDetail): string {
  if (run.kind === 'org_lookup') return ORG_LOOKUP_FAILED[orgLookupDepth(run)];
  if (run.kind === 'event_research') return 'Event research failed';
  if (run.kind === 'event_scan') return 'Scan failed';
  if (run.kind === 'prose_refresh') return 'Rewrite failed';
  if (run.research_pass === 'refresh') return 'Refresh failed';
  return 'Research failed';
}

function runningWord(run: RunDetail, subjectName: string | null): string {
  switch (run.kind) {
    case 'org_lookup':
      return orgLookupRunningLead(orgLookupDepth(run), subjectName);
    case 'quick_search':
      return 'Searching';
    case 'event_research':
      return 'Researching the event';
    case 'event_scan':
      return 'Scanning attendees';
    case 'report_generate':
      return 'Writing the report';
    case 'prose_refresh':
      return 'Rewriting the brief';
    default:
      return run.research_pass === 'refresh' ? 'Refreshing' : 'Researching';
  }
}

/** The line's word, what follows it, and how loud it is. */
export function runBlockHead(
  run: RunDetail,
  resuming: boolean,
  openQuestions: number,
  silent: boolean,
  subjectName: string | null = null,
  /** An on-chain identity pick is open on this run. */
  onchainPick = false,
): { head: string; tail: string | null; tone: Tone } {
  if (resuming) return { head: 'Resuming…', tail: null, tone: 'working' };
  switch (run.state) {
    case 'created':
      return { head: 'Starting…', tail: null, tone: 'working' };
    case 'checkpointed':
      return { head: 'Continuing…', tail: null, tone: 'working' };
    case 'running': {
      const head = runningWord(run, subjectName);
      const tail = run.kind === 'research' && run.current_round > 0 ? 'Follow-up round' : null;
      return { head, tail, tone: silent ? 'quiet' : 'working' };
    }
    case 'awaiting_identity_pick':
      return { head: RUN_CONFIRM_IDENTITY, tail: null, tone: 'act' };
    case 'awaiting_prose':
      return { head: RUN_MORE_DETAIL, tail: null, tone: 'act' };
    case 'awaiting_adjudication':
      // Parked on the pick alone: say what it waits for, not "findings".
      if (onchainPick && openQuestions === 0) {
        return { head: 'Paused', tail: RUN_PICK_ONCHAIN_IDENTITY, tone: 'act' };
      }
      return { head: 'Paused', tail: reviewBeforeBriefLine(openQuestions), tone: 'act' };
    case 'failed':
      if (isWithdrawnRun(run) || isCancelledRun(run)) {
        return { head: stripLine(run, false, openQuestions), tail: null, tone: 'stopped' };
      }
      return { head: failedWord(run), tail: null, tone: 'act' };
    case 'complete':
      if (run.delivered === false)
        return { head: RUN_UNDELIVERED_HEAD, tail: undeliveredLine(run.kind), tone: 'done' };
      return {
        head:
          run.kind === 'org_lookup'
            ? ORG_LOOKUP_DONE[orgLookupDepth(run)]
            : runCompleteHead(run.kind),
        tail: null,
        tone: 'done',
      };
    default:
      return { head: stripLine(run, resuming, openQuestions), tail: null, tone: 'working' };
  }
}

/**
 * The line under the head. A failed run whose Resume is waiting for the agent
 * (`resuming` off `run.resume`) says it is starting again; one with Resume on
 * offer says what Resume does.
 */
export function runBlockNote(
  run: RunDetail,
  tone: Tone,
  resuming: boolean,
  resumable: boolean,
): string | null {
  if (tone === 'quiet') return RUN_BLOCK_NOTES.quiet;
  if (tone === 'working' && !resuming) {
    return run.kind === 'research' ? RUN_BLOCK_NOTES.working : RUN_BLOCK_NOTES.workingPlain;
  }
  if (run.state === 'failed' && resuming) return RUN_BLOCK_NOTES.resumeQueued;
  return resumable ? RUN_BLOCK_NOTES.failed : null;
}

/** One segment per planned section, done ones first, then the ones in flight. */
function meterOf(run: RunDetail): { done: number; working: number; planned: number } | null {
  if (run.kind !== 'research' || run.current_round !== 0) return null;
  const planned = run.facet_map.planned;
  if (planned.length === 0) return null;
  const doneSet = new Set(
    run.facet_map.completions.filter((c) => c.round === run.current_round).map((c) => c.facet),
  );
  const working = planned.filter(
    (facet) => !doneSet.has(facet) && WORKING_PHASES.has(run.facet_progress?.[facet]?.phase ?? ''),
  ).length;
  return { done: planned.filter((f) => doneSet.has(f)).length, working, planned: planned.length };
}

/** "Reading sources for Reputation. Gathering sources for Network." */
function nowLine(run: RunDetail): ReactNode {
  if (run.kind === 'research' && run.facet_progress) {
    const live = run.facet_map.planned.filter((facet) =>
      WORKING_PHASES.has(run.facet_progress?.[facet]?.phase ?? ''),
    );
    if (live.length > 0) {
      return live.map((facet, index) => {
        const phase = FACET_PHASE_LABELS[run.facet_progress![facet]!.phase];
        return (
          <span key={facet}>
            {index > 0 ? ' ' : null}
            {phase.charAt(0).toUpperCase() + phase.slice(1)} for <b>{facetLabel(facet)}</b>.
          </span>
        );
      });
    }
  }
  const note = run.progress?.note ?? null;
  return note ? note.charAt(0).toUpperCase() + note.slice(1) : null;
}

function Mark({ tone }: { tone: Tone }) {
  if (tone === 'done') {
    return (
      <svg
        className="rb-tick"
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m3 7.25 2.5 2.5L11 4.25" />
      </svg>
    );
  }
  return <span className={`rb-mark ${tone}`} aria-hidden="true" />;
}

function RunDetailDisclosure({ run }: { run: RunDetail }) {
  const [open, setOpen] = useState(false);
  const rows = timelineRows(run);
  const ledger = run.kind === 'research' && run.facet_map.planned.length > 0;
  const parts = [
    ledger ? 'sections' : null,
    run.dedup?.resolutions ? 'duplicates' : null,
    'timeline',
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <div className="rb-detail">
      <button
        type="button"
        className="disclose-plus"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {RUN_DETAIL_LABEL}
        <span className="quiet"> · {parts}</span>
      </button>
      {open ? (
        <div className="rb-detail-body">
          {ledger ? <FacetLedger run={run} /> : null}
          {run.dedup ? <DedupLine dedup={run.dedup} /> : null}
          <ul className="rb-timeline">
            {rows.map((row) => (
              <li key={row.key}>
                <span className="t">{row.label}</span>
                <span className="at">{formatClock(row.at)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** The run block's three columns; the notes line above the brief shares them. */
export function Shell({
  tone,
  label,
  when,
  children,
  side,
  leaving = false,
}: {
  tone: Tone;
  label: string;
  when: string;
  children: ReactNode;
  side?: ReactNode;
  leaving?: boolean;
}) {
  return (
    <section
      className={leaving ? 'run-block leaving' : 'run-block'}
      data-tone={tone}
      aria-label={label}
    >
      <div className="rb-label">
        <span className="sc">{label}</span>
        <span className="when">{when}</span>
      </div>
      <div className="rb-main">{children}</div>
      <div className="rb-side">{side}</div>
    </section>
  );
}

export function RunBlock({
  subjectId,
  subjectName = null,
  run,
  pendingDecisions,
  pollFailures,
}: {
  subjectId: string;
  /** An organization's name, for its lookup's line. A person's run needs none. */
  subjectName?: string | null;
  run: RunDetail;
  pendingDecisions: DecisionSummary[];
  pollFailures: number;
}) {
  const controls = useRunControls(subjectId, run, pendingDecisions);
  const {
    resuming,
    silentFor,
    cancellable,
    openAdjudications,
    pickId,
    proseId,
    onchainPickId,
    cancel,
  } = controls;
  const { head, tail, tone } = runBlockHead(
    run,
    resuming,
    controls.openQuestions,
    silentFor !== null,
    subjectName,
    onchainPickId !== null,
  );
  const meter = meterOf(run);
  const moving = tone === 'working' || tone === 'quiet';
  const now = moving && !resuming ? nowLine(run) : null;
  const failure = run.state === 'failed' && tone === 'act' && run.error ? run.error.message : null;
  const note = runBlockNote(run, tone, resuming, controls.resumable);

  return (
    <>
      <Shell
        tone={tone}
        label={
          run.kind === 'org_lookup'
            ? ORG_RUN_LABELS[orgLookupDepth(run)]
            : runBlockLabel(run.kind, run.research_pass)
        }
        when={`Started ${formatClock(run.created_at)}`}
        side={
          cancellable ? (
            <button
              type="button"
              className="rb-stop"
              disabled={cancel.isPending}
              onClick={() => controls.setStopping(true)}
            >
              {cancel.isPending ? RUN_CANCEL.working : RUN_CANCEL.action}
            </button>
          ) : null
        }
      >
        <p className="rb-head" aria-live="polite">
          <Mark tone={tone} />
          <span className={tone === 'act' ? 'word act-word' : 'word'}>{head}</span>
          {tail ? <span className="tail">{tail}</span> : null}
          {silentFor !== null ? <span className="silent">· no signal for {silentFor}m</span> : null}
          {pollFailures >= 3 ? <span className="silent">· reconnecting</span> : null}
        </p>

        {meter ? (
          <div className="rb-meter">
            <div
              className="rb-segs"
              style={{ gridTemplateColumns: `repeat(${meter.planned}, minmax(0, 1fr))` }}
              aria-hidden="true"
            >
              {Array.from({ length: meter.planned }, (_, index) => (
                <span
                  key={index}
                  className={
                    index < meter.done
                      ? 'rb-seg done'
                      : index < meter.done + meter.working && moving
                        ? tone === 'quiet'
                          ? 'rb-seg stale'
                          : 'rb-seg on'
                        : 'rb-seg'
                  }
                />
              ))}
            </div>
            <span className="count">
              {sectionsDoneLine(meter.done, meter.planned)}
              {run.state === 'failed' ? ` ${RUN_BLOCK_NOTES.beforeStopped}` : null}
            </span>
          </div>
        ) : null}

        {now ? <p className="rb-now">{now}</p> : null}
        {failure ? <p className="rb-error">{failure}</p> : null}
        {note ? <p className="rb-note">{note}</p> : null}

        {controls.resumable ? (
          <div className="rb-act">
            <ResumeAction run={run} controls={controls} />
          </div>
        ) : null}

        {!resuming && run.state === 'awaiting_identity_pick' && pickId ? (
          <div className="rb-ask">
            <PickList
              subjectId={subjectId}
              decisionId={pickId}
              runId={run.id}
              runState="awaiting_identity_pick"
            />
          </div>
        ) : null}

        {!resuming && run.state === 'awaiting_prose' && proseId ? (
          <div className="rb-ask">
            <MoreProsePrompt
              subjectId={subjectId}
              decisionId={proseId}
              runId={run.id}
              runState="awaiting_prose"
            />
          </div>
        ) : null}

        {!resuming && run.state === 'awaiting_adjudication' && onchainPickId ? (
          <OnchainEntityPick subjectId={subjectId} decisionId={onchainPickId} runId={run.id} />
        ) : null}

        {!resuming && run.state === 'awaiting_adjudication' && openAdjudications.length > 0 ? (
          <FindingReview subjectId={subjectId} run={run} decisions={openAdjudications} />
        ) : null}

        <RunDetailDisclosure run={run} />
      </Shell>
      <StopRun run={run} controls={controls} />
    </>
  );
}

/**
 * **Whether to say "Brief ready"**: only when the page watched this very run
 * live and the record now reads it complete. A record already complete when
 * the page opened has no watched run, so a later visit or a reload never shows
 * it (blend spec, "Run complete").
 */
export function finishedWhileWatching(
  watched: RunDetail | null,
  latest: RunSummary | null,
): boolean {
  return (
    watched !== null && latest !== null && latest.id === watched.id && latest.state === 'complete'
  );
}

/**
 * The run just finished while the person was on the page. The record has
 * already moved on, so this is drawn from what the page saw, and it gives way
 * to the brief on its own (`leaving`).
 */
export function RunCompleteBlock({
  kind,
  delivered,
  startedAt,
  finishedAt,
  tail,
  leaving,
}: {
  kind: RunKind;
  delivered: boolean | null;
  startedAt: string;
  finishedAt: string | null;
  tail: string | null;
  leaving: boolean;
}) {
  return (
    <Shell
      tone="done"
      label={runBlockLabel(kind, null)}
      when={
        finishedAt ? `Finished ${formatClock(finishedAt)}` : `Started ${formatClock(startedAt)}`
      }
      leaving={leaving}
    >
      <p className="rb-head" aria-live="polite">
        <Mark tone="done" />
        <span className="word">
          {delivered === false ? RUN_UNDELIVERED_HEAD : runCompleteHead(kind)}
        </span>
        {delivered === false ? (
          <span className="tail">{undeliveredLine(kind)}</span>
        ) : tail ? (
          <span className="tail">{tail}</span>
        ) : null}
      </p>
      <p className="rb-note">{RUN_BLOCK_NOTES.complete}</p>
    </Shell>
  );
}
